import {
  getAllFunnels,
  getMessageHistory,
  getMessageStatistics,
  getPerformanceComparison,
  getStatisticsPerformanceOverview,
} from 'API/linkedin-funnel';
import { endOfWeek, setHours, setMilliseconds, setMinutes, setSeconds, startOfWeek } from 'date-fns';
import { ProductGroups } from 'enums';
import { Funnel, FunnelStatuses, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import ContactDrawer from '../campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import useContactDrawer from '../campaign-builder/use-contact-drawer';
import { useLinkedinAccountsQuery } from '../linkedin-accounts/hooks';
import { campaignsForAccounts, selectionForAccounts } from './statistics-account-filter';
import { LinkedinContact, PerformanceComparison, PerformanceOverview, Statistics } from './campaign-statistics-types';
import StatisticsHeader from './statistics-header';
import StatisticsMessagesOverview from './statistics-messages-overview';
import StatisticsPerformanceOverview from './statistics-performance-overview';

interface Props {
  productGroup: ProductGroups;
}

const CampaignStatistics: React.FC<Props> = ({ productGroup }) => {
  const { classes } = useStyles();
  useUserData();
  const { t } = useTranslation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const [campaigns, setCampaigns] = useState<Funnel[]>([]);
  const timeZone = 'Europe/Berlin';
  const [campaignFilter, setCampaignFilter] = useState<string[]>([]);
  const [linkedinAccountFilter, setLinkedinAccountFilter] = useState<string[]>([]);

  const { data: linkedinAccounts = [], error: linkedinAccountsError } = useLinkedinAccountsQuery({ prioritizeCurrentUser: true });

  /**
   * Der Account ist die obere Stufe des Drill-Downs: Er entscheidet, welche Kampagnen zur
   * Auswahl stehen. Gefiltert wird auf der bereits geladenen Liste, ein zweiter Abruf wäre
   * dafür nicht nötig.
   */
  const visibleCampaigns = useMemo(() => campaignsForAccounts(campaigns, linkedinAccountFilter), [campaigns, linkedinAccountFilter]);

  const now = new Date();
  const initialFromDate = setHours(setMinutes(setSeconds(setMilliseconds(startOfWeek(now, { weekStartsOn: 1 }), 0), 0), 0), 0);
  const initialToDate = setHours(setMinutes(setSeconds(setMilliseconds(endOfWeek(now, { weekStartsOn: 1 }), 999), 59), 59), 23);
  const [fromDate, setFromDate] = useState<Date>(initialFromDate);
  const [toDate, setToDate] = useState<Date>(initialToDate);
  const [showOverall, setShowOverall] = useState(false);

  const [performanceOverview, setPerformanceOverview] = useState<PerformanceOverview>();
  const [performanceOverviewLoading, setPerformanceOverviewLoading] = useState(false);

  const [performanceOfMessages, setPerformanceOfMessages] = useState<Statistics>();
  const [performanceOfMessagesLoading, setPerformanceOfMessagesLoading] = useState(false);

  const [messageHistory, setMessageHistory] = useState<LinkedinContact[]>([]);
  const [messageHistoryLoading, setMessageHistoryLoading] = useState(false);

  const [performanceComparison, setPerformanceComparison] = useState<PerformanceComparison[]>([]);
  const [performanceComparisonLoading, setPerformanceComparisonLoading] = useState(false);

  const messagePerformanceLoading = performanceOfMessagesLoading || messageHistoryLoading || performanceComparisonLoading;

  const { contactDrawer, handleOpenContactDrawer, handleUpdateDrawerContactIsActive, handleCloseContactDrawer } = useContactDrawer();

  const showStatisticsContent = () => {
    return (
      !_.isNil(performanceOverview) ||
      performanceOverviewLoading ||
      !_.isNil(performanceOfMessages) ||
      performanceOfMessagesLoading ||
      !_.isEmpty(messageHistory) ||
      messageHistoryLoading ||
      !_.isEmpty(performanceComparison) ||
      performanceComparisonLoading
    );
  };

  const type =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
      ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
      : undefined;

  const controllersRef = useRef(new Map<string, AbortController>());

  const createAbortController = (key: string) => {
    if (controllersRef.current.has(key)) {
      controllersRef.current.get(key)?.abort();
    }

    const controller = new AbortController();
    controllersRef.current.set(key, controller);
    return controller;
  };

  const fetchPerformanceOverview = async (
    newFunnels: string[] = campaignFilter,
    newTimeFrame: { from: Date; to: Date } = { from: fromDate, to: toDate },
    newShowOverall: boolean = showOverall
  ) => {
    const controller = createAbortController('performanceOverview');
    setPerformanceOverviewLoading(true);

    const res = await getStatisticsPerformanceOverview(newFunnels, newShowOverall ? 'all' : newTimeFrame, controller.signal);

    if (!res.error) {
      setPerformanceOverview(res.response);
      setPerformanceOverviewLoading(false);
    } else {
      if (res.error.name !== 'CanceledError') {
        setPerformanceOverviewLoading(false);
      }
      handleOpenErrorDialog(res.error, '/analytics/performance/overview', 'post');
    }
  };

  const fetchPerformanceOfMessages = async (
    newFunnels: string[] = campaignFilter,
    newTimeFrame: { from: Date; to: Date } = { from: fromDate, to: toDate },
    newShowOverall: boolean = showOverall
  ) => {
    const controller = createAbortController('performanceOfMessages');
    setPerformanceOfMessagesLoading(true);

    const res = await getMessageStatistics(newFunnels, newShowOverall ? 'all' : newTimeFrame, controller.signal);

    if (!res.error) {
      setPerformanceOfMessages(res.response);
      setPerformanceOfMessagesLoading(false);
    } else {
      if (res.error.name !== 'CanceledError') {
        setPerformanceOfMessagesLoading(false);
      }
      handleOpenErrorDialog(res.error, '/analytics/messages/statistics', 'post');
    }
  };

  const fetchPerformanceComparison = async (
    newFunnels: string[] = campaignFilter,
    newTimeFrame: { from: Date; to: Date } = { from: fromDate, to: toDate },
    newShowOverall: boolean = showOverall
  ) => {
    const controller = createAbortController('performanceComparison');
    setPerformanceComparisonLoading(true);

    const res = await getPerformanceComparison(newFunnels, newShowOverall ? 'all' : newTimeFrame, controller.signal);

    if (!res.error) {
      setPerformanceComparison(res);
      setPerformanceComparisonLoading(false);
    } else {
      if (res.error.name !== 'CanceledError') {
        setPerformanceComparisonLoading(false);
      }
      handleOpenErrorDialog(res.error, '/analytics/performance/comparison', 'post');
    }
  };

  const fetchMessageHistory = async (
    newFunnels: string[] = campaignFilter,
    newTimeFrame: { from: Date; to: Date } = { from: fromDate, to: toDate },
    newShowOverall: boolean = showOverall
  ) => {
    const controller = createAbortController('messageHistory');
    setMessageHistoryLoading(true);

    const res = await getMessageHistory(newFunnels, newShowOverall ? 'all' : newTimeFrame, controller.signal);

    if (!res.error) {
      setMessageHistory(res);
      setMessageHistoryLoading(false);
    } else {
      if (res.error.name !== 'CanceledError') {
        setMessageHistoryLoading(false);
      }
      handleOpenErrorDialog(res.error, '/messages/history', 'post');
    }
  };

  const fetchStatistics = (
    newFunnels: string[] = campaignFilter
  ) => {
    return () => {
      fetchPerformanceOverview(newFunnels);
      fetchPerformanceOfMessages(newFunnels);
      fetchMessageHistory(newFunnels);
      fetchPerformanceComparison(newFunnels);
    };
  };

  const fetchFunnels = async () => {
    const res = await getAllFunnels(type, undefined, undefined, undefined, undefined, undefined, undefined, true);
    if (res && !res.error) {
      const resCampaigns: Funnel[] = res.campaigns || [];
      const filteredCampaigns = resCampaigns.filter((campaign) => campaign.status !== FunnelStatuses.CREATED);

      setCampaigns(filteredCampaigns);

      if (!_.isEmpty(filteredCampaigns)) {
        const preselectedFunnelId = localStorage.getItem('preselect-funnel-statistics');

        if (!_.isNil(preselectedFunnelId)) {
          const campaignId = preselectedFunnelId;
          const newValue = typeof campaignId === 'string' ? campaignId.split(',') : campaignId;
          setCampaignFilter(newValue);
          fetchStatistics(newValue)();
          localStorage.removeItem('preselect-funnel-statistics');
        }
      }
    } else {
      setCampaigns([]);
      handleOpenErrorDialog(res?.error, '/campaigns', 'get');
    }
  };

  const campaignIdToName = (id: string) => {
    const foundCampaign = campaigns.find((campaign) => campaign._id === id);

    if (foundCampaign) {
      return foundCampaign.name;
    }

    return '-';
  };

  useEffect(() => {
    fetchFunnels();
  }, []);

  useEffect(() => {
    if (!_.isNil(linkedinAccountsError)) {
      handleOpenErrorDialog(linkedinAccountsError, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  }, [linkedinAccountsError, handleOpenErrorDialog]);

  /**
   * Filter option handlers
   */

  const handleLinkedinAccountFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;

    setLinkedinAccountFilter(newValue);
    setCampaignFilter((currentSelection) => selectionForAccounts(currentSelection, campaigns, newValue));
  };

  const handleCampaignFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    setCampaignFilter(newValue);
  };

  const handleFromDateChange = (date: Date) => {
    // Always set to 00:00:00.000
    const normalized = setHours(setMinutes(setSeconds(setMilliseconds(date, 0), 0), 0), 0);
    setFromDate(normalized);
  };

  const handleToDateChange = (date: Date) => {
    // Always set to 23:59:59.999
    const normalized = setHours(setMinutes(setSeconds(setMilliseconds(date, 999), 59), 59), 23);
    setToDate(normalized);
  };

  const handleShowOverallChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { checked },
    } = event;
    setShowOverall(checked);
  };

  return (
    <>
      <StatisticsHeader
        linkedinAccounts={linkedinAccounts}
        linkedinAccountFilter={linkedinAccountFilter}
        campaigns={visibleCampaigns}
        campaignFilter={campaignFilter}
        fromDate={fromDate}
        toDate={toDate}
        showOverall={showOverall}
        handleLinkedinAccountFilterChange={handleLinkedinAccountFilterChange}
        handleCampaignFilterChange={handleCampaignFilterChange}
        handleFromDateChange={handleFromDateChange}
        handleToDateChange={handleToDateChange}
        handleShowOverallChange={handleShowOverallChange}
        fetchStatistics={fetchStatistics}
      />
      {showStatisticsContent() ? (
        <div className={classes.statistics}>
          <StatisticsPerformanceOverview performanceOverview={performanceOverview} loading={performanceOverviewLoading} />
          <StatisticsMessagesOverview
            performanceOfMessages={performanceOfMessages}
            timeZone={timeZone}
            messageHistory={messageHistory}
            performanceComparison={performanceComparison}
            campaignIdToName={campaignIdToName}
            loading={messagePerformanceLoading}
            contactDrawerLoading={contactDrawer.loading}
            handleOpenContactDrawer={handleOpenContactDrawer}
          />
        </div>
      ) : (
        <div className={classes.noCampaignSelected}>
          <p>{t('noCampaignStartedYet')}</p>
        </div>
      )}

      <ContactDrawer
        open={contactDrawer.open}
        productGroup={productGroup}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  statistics: {
    background: theme.palette.backgrounds.main_new,
    overflow: 'hidden',
    padding: '0 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  noCampaignSelected: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.backgrounds.main_new,
    minHeight: '250px',
    textAlign: 'center',
    padding: '1rem',
    '& p': {
      fontSize: theme.typography.pxToRem(20),
      maxWidth: '600px',
    },
  },
}));

export default CampaignStatistics;
